import React, { useEffect, useState } from "react";
import { recordRecv } from "@/lib/dataMeter";
import { ultraLiteOn } from "@/lib/prefs";

const MEDIA_CACHE = "rc-media-v1";
const INDEX_KEY = "rc_media_index"; // { [cacheKey]: cachedAtMs } for auto-delete TTL

function readIndex() { try { return JSON.parse(localStorage.getItem(INDEX_KEY)) || {}; } catch { return {}; } }
function writeIndex(idx) { try { localStorage.setItem(INDEX_KEY, JSON.stringify(idx)); } catch {} }
function touchIndex(key) { const idx = readIndex(); idx[key] = Date.now(); writeIndex(idx); }

// Delete cached media older than `days` from the Cache API + index. days<=0 disables.
export async function purgeOldMedia(days) {
  if (!days || days <= 0 || typeof caches === "undefined") return { removed: 0 };
  const cutoff = Date.now() - days * 864e5;
  const idx = readIndex();
  let removed = 0;
  try {
    const cache = await caches.open(MEDIA_CACHE);
    for (const [key, ts] of Object.entries(idx)) {
      if (ts < cutoff) {
        try { await cache.delete(key); } catch {}
        delete idx[key];
        removed++;
      }
    }
    writeIndex(idx);
  } catch {}
  return { removed };
}

// Decides whether media should auto-download based on the user's per-type setting.
// rc_autodl_types: { image, audio, video, document } booleans. Image defaults on.
const AUTODL_KEY = "rc_autodl_types";
const DL_INDEX_KEY = "rc_downloaded_media"; // media the user has already downloaded (persists across sessions)
const DEFAULT_AUTODL = { image: true, audio: false, video: false, document: false };
const KIND_TO_TYPE = { image: "image", gif: "image", album: "image", sticker: "image", video: "video", audio: "audio", document: "document" };

export function getAutoDlTypes() {
  try { return { ...DEFAULT_AUTODL, ...(JSON.parse(localStorage.getItem(AUTODL_KEY)) || {}) }; }
  catch { return { ...DEFAULT_AUTODL }; }
}
export function setAutoDlType(type, on) {
  const cur = getAutoDlTypes();
  cur[type] = !!on;
  try { localStorage.setItem(AUTODL_KEY, JSON.stringify(cur)); } catch {}
  return cur;
}

function mediaKeyOf(k) {
  let s = (k || "").split("?")[0];
  try { if (/^https?:/.test(s)) s = new URL(s).pathname; } catch {}
  return s;
}
export function isMediaDownloaded(k) {
  if (!k) return false;
  try { return !!(JSON.parse(localStorage.getItem(DL_INDEX_KEY)) || {})[mediaKeyOf(k)]; } catch { return false; }
}
export function markMediaDownloaded(k) {
  if (!k) return;
  try { const idx = JSON.parse(localStorage.getItem(DL_INDEX_KEY)) || {}; idx[mediaKeyOf(k)] = 1; localStorage.setItem(DL_INDEX_KEY, JSON.stringify(idx)); } catch {}
}
export function clearDownloadedIndex() { try { localStorage.removeItem(DL_INDEX_KEY); } catch {} }

export function autoDownloadAllowed(kind = "image") {
  if (ultraLiteOn()) return false; // Ultra-Lite: always tap-to-load media
  const type = KIND_TO_TYPE[kind] || "image";
  return !!getAutoDlTypes()[type];
}

// Resolve a media URL to a cached blob URL (Cache API), fetching+caching on first open.
// Falls back to the network URL if caching is unavailable. Ignores the token query in the cache key.
export function useCachedMedia(url) {
  const [src, setSrc] = useState(url);

  useEffect(() => {
    setSrc(url);
    if (!url || !/^https?:/.test(url) || typeof caches === "undefined") return;
    let objectUrl;
    let active = true;
    (async () => {
      try {
        const key = url.split("?")[0]; // drop token so the key is stable across sessions
        const cache = await caches.open(MEDIA_CACHE);
        let res = await cache.match(key);
        let fromNetwork = false;
        if (!res) {
          res = await fetch(url);
          fromNetwork = true;
          if (res && res.ok) { await cache.put(key, res.clone()); touchIndex(key); }
        }
        if (res && res.ok && active) {
          const blob = await res.blob();
          if (fromNetwork) recordRecv(blob.size); // count real downloaded bytes
          markMediaDownloaded(key);
          objectUrl = URL.createObjectURL(blob);
          setSrc(objectUrl);
        }
      } catch (e) {
        // keep the network URL
      }
    })();
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [url]);

  return src;
}

// <img> that serves from the on-device media cache once the file has been opened.
export function CachedImg({ src, ...props }) {
  const resolved = useCachedMedia(src);
  return <img src={resolved} {...props} />;
}
